import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type ShortUrlResponse } from '@air/api/types';

import { OnOffShareToggleRow } from '~/components/ShareModal/components/OnOffShareToggleRow';
import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { ToggleInfoTooltip } from '~/components/ShareModal/components/ToggleInfoTooltip';
import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import { type ShareModalUpdateResponse } from '~/components/ShareModal/types/shareModalUpdateResponse';
import { currentConfigurableViewSelector } from '~/store/configViews/selectors';
import {
  shareUrlUpdatedDisableExpandingNested,
  shareUrlUpdatedDisableViewSettings,
  shareUrlUpdatedHideSearchInput,
} from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type VisibilityPermissionsSectionProps = {
  shortIdInfo: ShortUrlResponse;
  isLoading: boolean;
  onShortUrlUpdate: OnShortUrlUpdate<ShareModalUpdateResponse>;
};

export const VisibilityPermissionsSection = memo(
  ({ shortIdInfo, isLoading, onShortUrlUpdate }: VisibilityPermissionsSectionProps) => {
    const currentConfigurableView = useSelector(currentConfigurableViewSelector);

    const hideSearchBar = useMemo(
      () => shortIdInfo.presentationSettings?.hideSearchBar === true,
      [shortIdInfo.presentationSettings?.hideSearchBar],
    );

    const isExpandNestedDisabled = useMemo(
      () => shortIdInfo.presentationSettings?.lockedControls?.expandNested === true,
      [shortIdInfo.presentationSettings?.lockedControls?.expandNested],
    );

    const handleToggleHideSearchBar = useCallback(
      (checked: boolean) =>
        onShortUrlUpdate({
          props: { presentationSettings: { hideSearchBar: checked } },
          analytics: shareUrlUpdatedHideSearchInput(checked),
        }),
      [onShortUrlUpdate],
    );

    const disableViewSettingsChanges = useMemo(() => {
      const lockedControls = shortIdInfo.presentationSettings?.lockedControls;

      return (
        lockedControls?.viewType === true &&
        lockedControls?.cardSize === true &&
        lockedControls?.thumbnailStyle === true &&
        lockedControls?.metadataFieldsVisible === true
      );
    }, [shortIdInfo.presentationSettings?.lockedControls]);

    const handleToggleDisableViewSettings = useCallback(
      (checked: boolean) => {
        if (!checked) {
          // OFF writes each field explicitly to `false` rather than `lockedControls: undefined`.
          // The backend patch-merges `lockedControls`, so a bare `undefined` would wipe every
          // sibling field — including `expandNested` from COR-1939 and any future locked-controls
          // fields. Setting only the four fields this toggle owns preserves siblings.
          void onShortUrlUpdate({
            props: {
              presentationSettings: {
                lockedControls: {
                  viewType: false,
                  cardSize: false,
                  thumbnailStyle: false,
                  metadataFieldsVisible: false,
                },
              },
            },
            analytics: shareUrlUpdatedDisableViewSettings(false),
          });
          return;
        }

        // ON locks the four fields and snapshots the creator's current view into `defaultView`
        // so public viewers boot to that exact layout.
        void onShortUrlUpdate({
          props: {
            presentationSettings: {
              lockedControls: {
                viewType: true,
                cardSize: true,
                thumbnailStyle: true,
                metadataFieldsVisible: true,
              },
              defaultView: {
                viewType: currentConfigurableView?.viewType,
                cardSize: currentConfigurableView?.cardSize,
                thumbnailStyle: currentConfigurableView?.thumbnailStyle,
                metadataFieldsVisible: currentConfigurableView?.metadataFieldsVisible,
              },
            },
          },
          analytics: shareUrlUpdatedDisableViewSettings(true),
        });
      },
      [onShortUrlUpdate, currentConfigurableView],
    );

    const handleToggleDisableExpandNested = useCallback(
      (checked: boolean) =>
        onShortUrlUpdate({
          props: {
            presentationSettings: { lockedControls: { expandNested: checked } },
          },
          analytics: shareUrlUpdatedDisableExpandingNested(checked),
        }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Visibility & permissions" />
        <OnOffShareToggleRow
          label="Hide search input"
          labelAccessory={<ToggleInfoTooltip text="Hide the search bar from public viewers" />}
          checked={hideSearchBar}
          isLoading={isLoading}
          onToggle={handleToggleHideSearchBar}
          data-testid="HIDE_SEARCH_BAR_TOGGLE"
        />
        <OnOffShareToggleRow
          label="Disable expanding nested content"
          labelAccessory={<ToggleInfoTooltip text="Prevent viewers from expanding nested content" />}
          checked={isExpandNestedDisabled}
          isLoading={isLoading}
          onToggle={handleToggleDisableExpandNested}
          data-testid="DISABLE_EXPAND_NESTED_TOGGLE"
        />
        <OnOffShareToggleRow
          label="Disable view settings changes"
          labelAccessory={
            <ToggleInfoTooltip text="Prevent viewers from changing view type, card size, thumbnail style, and asset details visibility" />
          }
          checked={disableViewSettingsChanges}
          isLoading={isLoading}
          onToggle={handleToggleDisableViewSettings}
          data-testid="DISABLE_VIEW_SETTINGS_CHANGES_TOGGLE"
        />
      </div>
    );
  },
);

VisibilityPermissionsSection.displayName = 'VisibilityPermissionsSection';
